import React, { useState, useEffect } from 'react';

const CustomCursor: React.FC = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hidden, setHidden] = useState(true);
  const [clicked, setClicked] = useState(false);
  const [linkHovered, setLinkHovered] = useState(false);

  useEffect(() => {
    // Add event listeners
    const addEventListeners = () => {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseenter', onMouseEnter);
      document.addEventListener('mouseleave', onMouseLeave);
      document.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mouseup', onMouseUp);
    };

    // Remove event listeners
    const removeEventListeners = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseenter', onMouseEnter);
      document.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mouseup', onMouseUp);
    };

    // Mouse move handler
    const onMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
    };

    // Mouse enter handler
    const onMouseEnter = () => {
      setHidden(false);
    };

    // Mouse leave handler
    const onMouseLeave = () => {
      setHidden(true);
    };

    // Mouse down handler
    const onMouseDown = () => {
      setClicked(true);
    };

    // Mouse up handler
    const onMouseUp = () => {
      setClicked(false);
    };

    // Add event listeners for links and buttons
    const handleLinkHoverEvents = () => {
      const links = document.querySelectorAll('a, button, .card, input, [role="button"]');

      links.forEach(link => {
        link.addEventListener('mouseenter', () => setLinkHovered(true));
        link.addEventListener('mouseleave', () => setLinkHovered(false));
      });
    };

    // Add CSS to make sure the main cursor is visible and interactive elements show the pointer cursor
    const addCursorStyles = () => {
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        body {
          cursor: auto !important;
        }
        a, button, .card, input, [role="button"],
        .cursor-pointer, [data-clickable="true"],
        .link, .button, .nav-item, .clickable,
        .hover\\:cursor-pointer:hover {
          cursor: pointer !important;
        }
      `;
      document.head.appendChild(styleElement);
      return styleElement;
    };

    // Initial setup
    addEventListeners();
    const styleElement = addCursorStyles();

    // Setup link hover events with a small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      handleLinkHoverEvents();
    }, 1000);

    // Cleanup
    return () => {
      removeEventListeners();
      clearTimeout(timeout);
      if (styleElement && document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  return (
    <>
      <div
        className={`fixed pointer-events-none z-50 w-8 h-8 flex items-center justify-center transition-opacity duration-300 ${
          hidden ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div
          className={`rounded-full border transition-all duration-200 ${
            clicked
              ? 'w-5 h-5 bg-[#F670C7]/30 border-[#F670C7]'
              : linkHovered
              ? 'w-12 h-12 bg-[#7F56D9]/10 border-[#7F56D9]'
              : 'w-8 h-8 bg-transparent border-white/30'
          }`}
        ></div>
      </div>

      <div
        className={`fixed pointer-events-none z-50 rounded-full w-2 h-2 bg-white mix-blend-difference transition-opacity duration-300 ${
          hidden ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'translate(-50%, -50%)',
        }}
      ></div>
    </>
  );
};

export default CustomCursor;
